import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, Row, Col, Modal, Pagination, Spinner, Badge } from 'react-bootstrap';
import { FaSearch, FaPlus, FaFileExcel, FaFileAlt, FaUser, FaRegCalendarAlt, FaStore, FaRegFileImage } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';

// Dynamic loading of XLSX library to avoid build errors if not installed locally
const loadXLSX = () => {
    return new Promise((resolve, reject) => {
        if (window.XLSX) {
            resolve(window.XLSX);
            return;
        }
        const script = document.createElement('script');
        script.src = "https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js";
        script.onload = () => resolve(window.XLSX);
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const Receipts = () => {
    // ---------------------------------------------------------
    // 1. State Management
    // ---------------------------------------------------------
    const [receipts, setReceipts] = useState([]);
    const [postCount, setPostCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    // Search Filters (inputs)
    const [filters, setFilters] = useState({
        title: '',
        member_id: '',
        status: '',
        store_name: '',
        transaction_date: '',
        description: ''
    });

    // Effective Search Criteria (used for API)
    const [searchCriteria, setSearchCriteria] = useState({ ...filters });

    // Modals
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showRegistModal, setShowRegistModal] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    // Registration Form
    const [registForm, setRegistForm] = useState({
        title: '',
        description: '',
        attachment: null
    });
    const [registLoading, setRegistLoading] = useState(false);

    // ---------------------------------------------------------
    // 2. API Functions
    // ---------------------------------------------------------
    const fetchReceipts = React.useCallback(async (pageNumber = 1, criteria = searchCriteria) => {
        setLoading(true);
        try {
            const params = { ...criteria, page: pageNumber };
            const response = await axiosInstance.post('jdsys/receipts/list', params);
            if (response.data.status === '0000') {
                // Filter out invalid items where json is empty (as seen in some n8n responses)
                const validList = (response.data.data.dataList || []).filter(item => item.json && Object.keys(item.json).length > 0 && item.json.id);
                setReceipts(validList);
                setPostCount(response.data.data.postCount || 0);
            }
        } catch (error) {
            console.error('Failed to fetch receipts:', error);
            alert('리스트 조회에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [searchCriteria]);

    useEffect(() => {
        fetchReceipts(page);
    }, [page, fetchReceipts]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearchCriteria({ ...filters }); // Update effective criteria
    };

    const handleRegist = async (e) => {
        e.preventDefault();
        if (!registForm.title || !registForm.attachment) {
            alert('지결서명과 영수증 파일을 입력해주세요.');
            return;
        }

        setRegistLoading(true);
        try {
            const formData = new FormData();
            formData.append('title', registForm.title);
            formData.append('description', registForm.description);
            formData.append('attachment', registForm.attachment);

            const response = await axiosInstance.post('jdsys/receipts/regist', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.status === '0000') {
                alert(response.data.message || '정상적으로 등록 되었습니다.');
                setShowRegistModal(false);
                setRegistForm({ title: '', description: '', attachment: null });
                fetchReceipts(1);
            } else {
                alert(response.data.message || '등록에 실패했습니다.');
            }
        } catch (error) {
            console.error('Registration error:', error);
            alert('등록 중 오류가 발생했습니다.');
        } finally {
            setRegistLoading(false);
        }
    };

    // ---------------------------------------------------------
    // 3. Excel Export
    // ---------------------------------------------------------
    const downloadExcel = async () => {
        try {
            const completedReceipts = receipts
                .filter(item => item.json.status === 'C')
                .map((item, index) => ({
                    'NO': index + 1,
                    '날짜': item.json.transaction_date,
                    '항목': item.json.title,
                    '금액': item.json.total_amount,
                    '비고': item.json.description
                }));

            if (completedReceipts.length === 0) {
                alert('처리완료(C) 상태인 항목이 없습니다.');
                return;
            }

            const XLSX_LIB = await loadXLSX();
            const ws = XLSX_LIB.utils.json_to_sheet(completedReceipts);
            const wb = XLSX_LIB.utils.book_new();
            XLSX_LIB.utils.book_append_sheet(wb, ws, "지출결의서");
            XLSX_LIB.writeFile(wb, "지출결의서.xlsx");
        } catch (error) {
            console.error('Excel library load error:', error);
            alert('엑셀 라이브러리를 불러오는 데 실패했습니다.');
        }
    };

    // ---------------------------------------------------------
    // 4. Render Helpers
    // ---------------------------------------------------------
    const totalPages = Math.ceil(postCount / 10) || 1;

    const renderPagination = () => {
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item key={number} active={number === page} onClick={() => setPage(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
    };

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold">
                    <FaFileAlt className="text-primary me-2" />
                    지출결의서 관리 <small className="text-muted fs-6">주디오시스템즈(주)</small>
                </h2>
                <div className="d-flex gap-2">
                    <Button variant="outline-success" onClick={downloadExcel}>
                        <FaFileExcel className="me-1" /> 엑셀 다운로드
                    </Button>
                    <Button variant="primary" onClick={() => setShowRegistModal(true)}>
                        <FaPlus className="me-1" /> 지결서 등록
                    </Button>
                </div>
            </div>

            {/* Search Filter */}
            <div className="bg-light p-4 rounded-4 shadow-sm mb-4">
                <Form onSubmit={handleSearch}>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">지결서명</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="검색어 입력"
                                    value={filters.title}
                                    onChange={(e) => setFilters({ ...filters, title: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">등록자 ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="ID 입력"
                                    value={filters.member_id}
                                    onChange={(e) => setFilters({ ...filters, member_id: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">상태</Form.Label>
                                <Form.Select
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                >
                                    <option value="">전체</option>
                                    <option value="I">처리중</option>
                                    <option value="C">처리완료</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">결제일</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={filters.transaction_date}
                                    onChange={(e) => setFilters({ ...filters, transaction_date: e.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button variant="dark" type="submit" className="w-100">
                                <FaSearch className="me-1" /> 검색
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>

            {/* Table List */}
            <div className="bg-white rounded-4 shadow-sm overflow-hidden">
                <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th className="text-center py-3" style={{ width: '80px' }}>ID</th>
                            <th className="py-3">지결서명</th>
                            <th className="text-center py-3" style={{ width: '120px' }}>상태</th>
                            <th className="text-center py-3" style={{ width: '150px' }}>결제일</th>
                            <th className="text-end py-3" style={{ width: '150px' }}>총금액</th>
                            <th className="text-center py-3" style={{ width: '120px' }}>등록자</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <div className="mt-2 text-muted">데이터를 불러오는 중입니다...</div>
                                </td>
                            </tr>
                        ) : receipts.length > 0 ? (
                            receipts.map((item) => (
                                <tr key={item.json.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedReceipt(item.json); setShowDetailModal(true); }}>
                                    <td className="text-center text-muted">{item.json.id}</td>
                                    <td className="fw-medium">{item.json.title}</td>
                                    <td className="text-center">
                                        <Badge bg={item.json.status === 'C' ? 'success' : 'warning'} className="px-3 py-2 rounded-pill">
                                            {item.json.status === 'C' ? '처리완료' : '처리중'}
                                        </Badge>
                                    </td>
                                    <td className="text-center">{item.json.transaction_date}</td>
                                    <td className="text-end fw-bold text-primary">{item.json.total_amount?.toLocaleString()}원</td>
                                    <td className="text-center">{item.json.member_id}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-muted">
                                    데이터가 없습니다.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {renderPagination()}

            {/* Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered contentClassName="bg-white shadow-lg border-0">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">지출결의서 상세 정보</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {selectedReceipt && (
                        <div className="p-2">
                            <Row className="mb-4">
                                <Col md={7}>
                                    <div className="mb-3">
                                        <h4 className="text-primary mb-1">{selectedReceipt.title}</h4>
                                        <p className="text-muted small">{selectedReceipt.description}</p>
                                    </div>
                                    <Row className="g-3 small text-muted">
                                        <Col xs={6}><FaUser className="me-1" /> 등록자: {selectedReceipt.member_id}</Col>
                                        <Col xs={6}><FaRegCalendarAlt className="me-1" /> 결제일: {selectedReceipt.transaction_date}</Col>
                                        <Col xs={12}><FaStore className="me-1" /> 상점: {selectedReceipt.store_name}</Col>
                                    </Row>
                                    <div className="mt-4 p-3 bg-light rounded-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-bold">총 결제 금액</span>
                                            <span className="h4 mb-0 text-primary fw-bold">{selectedReceipt.total_amount?.toLocaleString()}원</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={5}>
                                    <div className="bg-light rounded-4 d-flex align-items-center justify-content-center overflow-hidden h-100" style={{ minHeight: '200px' }}>
                                        {selectedReceipt.image_path ? (
                                            <img
                                                src={`https://www.wajok.com${selectedReceipt.image_path}`}
                                                alt="영수증"
                                                className="img-fluid"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => window.open(`https://www.wajok.com${selectedReceipt.image_path}`, '_blank')}
                                                onError={(e) => { e.target.src = 'https://placehold.co/400x600?text=No+Image'; }}
                                            />
                                        ) : (
                                            <div className="text-center p-4">
                                                <FaRegFileImage size={40} className="text-muted mb-2" />
                                                <div className="small text-muted">이미지가 없습니다.</div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {selectedReceipt.items && selectedReceipt.items.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="fw-bold mb-3">세부 내역</h6>
                                    <Table size="sm" hover className="border">
                                        <thead className="bg-light small">
                                            <tr>
                                                <th className="px-3">상품명</th>
                                                <th className="text-end px-3">단가</th>
                                                <th className="text-center px-3">수량</th>
                                                <th className="text-end px-3">금액</th>
                                            </tr>
                                        </thead>
                                        <tbody className="small">
                                            {selectedReceipt.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-3 py-2">{item.item_name}</td>
                                                    <td className="text-end px-3 py-2">{item.price?.toLocaleString()}</td>
                                                    <td className="text-center px-3 py-2">{item.quantity}</td>
                                                    <td className="text-end px-3 py-2 fw-medium">{item.total_price?.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>닫기</Button>
                </Modal.Footer>
            </Modal>

            {/* Registration Modal */}
            <Modal show={showRegistModal} onHide={() => setShowRegistModal(false)} centered contentClassName="bg-white shadow-lg border-0">
                <Modal.Header closeButton className="border-0 shadow-sm">
                    <Modal.Title className="fw-bold">지출결의서 신규 등록</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleRegist}>
                    <Modal.Body className="py-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small">지결서명 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="예: 12월 사무용품 구매"
                                required
                                value={registForm.title}
                                onChange={(e) => setRegistForm({ ...registForm, title: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small">비고</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="추가 설명 입력"
                                value={registForm.description}
                                onChange={(e) => setRegistForm({ ...registForm, description: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-0">
                            <Form.Label className="fw-bold small">영수증 파일 <span className="text-danger">*</span></Form.Label>
                            <div className="p-4 border border-dashed rounded-3 text-center bg-light">
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    id="receipt-file"
                                    onChange={(e) => setRegistForm({ ...registForm, attachment: e.target.files[0] })}
                                />
                                <label htmlFor="receipt-file" style={{ cursor: 'pointer' }}>
                                    {registForm.attachment ? (
                                        <div>
                                            <FaRegFileImage className="text-primary mb-2" size={30} />
                                            <div className="small fw-bold">{registForm.attachment.name}</div>
                                            <div className="text-muted x-small">클릭하여 변경</div>
                                        </div>
                                    ) : (
                                        <div>
                                            <FaPlus className="text-muted mb-2" size={30} />
                                            <div className="small text-muted">파일을 선택하세요 (이미지 파일)</div>
                                        </div>
                                    )}
                                </label>
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="outline-secondary" onClick={() => setShowRegistModal(false)}>취소</Button>
                        <Button variant="primary" type="submit" disabled={registLoading}>
                            {registLoading ? <Spinner animation="border" size="sm" /> : '등록하기'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default Receipts;
